import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { X, Lock } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Calendar } from "../ui/calendar";
import { adminApi, formatApiErrorDetail } from "../../lib/adminApi";

const CAL = {
  months: "flex flex-col sm:flex-row gap-8",
  caption_label: "text-base font-serif text-ivory",
  nav_button: "h-8 w-8 border border-white/15 text-stone-300 hover:border-champagne hover:text-champagne inline-flex items-center justify-center bg-transparent",
  head_cell: "text-stone-500 w-11 font-mono text-[10px] uppercase tracking-widest",
  cell: "relative p-0 text-center text-sm",
  day: "h-11 w-11 p-0 font-light text-stone-200 hover:bg-white/10 inline-flex items-center justify-center transition-colors",
  day_today: "text-champagne underline underline-offset-4",
  day_outside: "text-stone-700",
  day_disabled: "text-stone-700 opacity-40",
};

export default function AdminAvailability({ onLogout }) {
  const [blocks, setBlocks] = useState([]);
  const [reason, setReason] = useState("");
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["availability"] });

  const load = async () => {
    try {
      const { data } = await adminApi.get("/availability/blocks");
      setBlocks(data);
    } catch (err) {
      if (err.response?.status === 401) return onLogout();
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const byDate = useMemo(() => Object.fromEntries(blocks.map((b) => [b.date, b])), [blocks]);
  const manualDates = blocks.filter((b) => b.source === "manual").map((b) => parseISO(b.date));
  const bookedDates = blocks.filter((b) => b.source === "enquiry").map((b) => parseISO(b.date));

  const removeBlock = async (b) => {
    try {
      await adminApi.delete(`/availability/blocks/${b.id}`);
      setBlocks((list) => list.filter((x) => x.id !== b.id));
      invalidate();
      toast.success(`${format(parseISO(b.date), "d MMM yyyy")} released`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggle = async (day) => {
    if (!day) return;
    const key = format(day, "yyyy-MM-dd");
    const existing = byDate[key];
    if (existing) return removeBlock(existing);
    try {
      const { data } = await adminApi.post("/availability/blocks", { date: key, reason: reason || "Unavailable" });
      setBlocks((list) => [...list, data].sort((a, b) => a.date.localeCompare(b.date)));
      invalidate();
      toast.success(`${format(day, "d MMM yyyy")} blocked`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-availability-panel" className="grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-7">
        <p className="text-[10px] font-mono tracking-[0.25em] uppercase text-stone-500">Calendar</p>
        <h2 className="mt-2 font-serif text-xl text-ivory">Click a date to <span className="italic text-champagne">block or release</span> it</h2>
        <div className="mt-4">
          <label htmlFor="block-reason" className="text-[9px] font-mono tracking-[0.25em] uppercase text-stone-500">Reason for new blocks (optional)</label>
          <input id="block-reason" data-testid="block-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Personal travel, editing week…" className="mt-1 w-full max-w-sm bg-transparent border-b border-white/15 focus:border-champagne outline-none py-2 text-sm font-light text-ivory placeholder:text-stone-600 transition-colors" />
        </div>
        <div className="mt-6 inline-block border border-white/10 bg-elevated p-4" data-testid="availability-calendar">
          <Calendar
            mode="single"
            numberOfMonths={2}
            onSelect={toggle}
            selected={undefined}
            modifiers={{ manual: manualDates, booked: bookedDates }}
            modifiersClassNames={{ manual: "manual-block-day", booked: "booked-day" }}
            classNames={CAL}
          />
          <div className="mt-3 flex flex-wrap items-center gap-5 px-1 text-[9px] font-mono tracking-[0.2em] uppercase text-stone-500">
            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 bg-gold" /> Booked wedding</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 bg-stone-500" /> Manually blocked</span>
          </div>
        </div>
      </div>

      <div className="lg:col-span-5">
        <p className="text-[10px] font-mono tracking-[0.25em] uppercase text-stone-500">Reserved Dates</p>
        <h2 className="mt-2 font-serif text-xl text-ivory">{blocks.length} date{blocks.length !== 1 && "s"} unavailable</h2>
        {blocks.length === 0 ? (
          <p data-testid="blocks-empty" className="mt-6 font-serif italic text-stone-500">Every date is currently open for enquiries.</p>
        ) : (
          <ul className="mt-6 space-y-2" data-testid="blocks-list">
            {blocks.map((b) => (
              <li key={b.id} data-testid={`block-item-${b.id}`} className="flex items-center justify-between gap-4 border border-white/10 bg-elevated px-4 py-3">
                <div>
                  <p className="font-serif text-ivory">{format(parseISO(b.date), "EEEE, d MMMM yyyy")}</p>
                  <p className="text-[10px] font-mono tracking-[0.15em] uppercase text-stone-500">{b.reason}</p>
                </div>
                {b.source === "enquiry" ? (
                  <span data-testid={`block-locked-${b.id}`} title="Held by a booked enquiry" className="inline-flex items-center gap-1 text-[9px] font-mono tracking-[0.2em] uppercase text-gold">
                    <Lock size={11} /> Booked
                  </span>
                ) : (
                  <button data-testid={`block-remove-${b.id}`} onClick={() => removeBlock(b)} className="p-2 text-stone-500 hover:text-terracotta transition-colors" aria-label="Release date">
                    <X size={14} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
